import {
  ValueDescription_T,
  type OutputDescription,
} from "@smartsuite/automation-engine-api";
import type { Fields } from "@smartsuite/smartsuite-client";
import { findWebhookAction } from "./find-webhook-action";
import type { LoopingStore } from "../../../../../../stores/automation/looping.store";
import type {
  ActionCandidate,
  TriggerCandidate,
} from "../../../../../../types/candidate";
import { getOutputDescriptions } from "../../../../../../utils/conditions-coverter.hook";
import { RECORD_CREATED_TRIGGER_IDS } from "../../../../../../utils/constants";

const LOOPING_SUPPORTED_FIELD_TYPES: Set<string> = new Set([
  "multipleselectfield",
  "userfield",
  "emailfield",
  "phonefield",
  "linkedrecordfield",
  "linkfield",
  "teamfield",
  "tagsfield",
  "ipaddressfield",
]);

const isExcludedFollowedByOutput = (output: OutputDescription): boolean => {
  const smartsuiteFieldType = output.smartsuiteFieldType?.toLowerCase();

  return (
    smartsuiteFieldType === "userfield" &&
    output.outputId.toLowerCase().includes("followed_by")
  );
};

const supportsRecordFieldLooping = (output: OutputDescription): boolean => {
  const smartsuiteFieldType = output.smartsuiteFieldType?.toLowerCase();

  if (isExcludedFollowedByOutput(output)) {
    return false;
  }

  return (
    smartsuiteFieldType !== undefined &&
    LOOPING_SUPPORTED_FIELD_TYPES.has(smartsuiteFieldType as Fields)
  );
};

const supportsActionOutputLooping = (output: OutputDescription): boolean => {
  if (isExcludedFollowedByOutput(output)) {
    return false;
  }

  return (
    output.value?.type === ValueDescription_T.LIST ||
    supportsRecordFieldLooping(output)
  );
};

const isRecordTriggerSource = (trigger: TriggerCandidate): boolean =>
  RECORD_CREATED_TRIGGER_IDS.includes(
    trigger.triggerReference?.triggerId ?? "",
  );

export const outputReferenceToOptionValue = (
  looping: LoopingStore,
  trigger: TriggerCandidate,
  allActions: ActionCandidate[],
): OutputDescription[] | undefined => {
  if (looping.source?.oneofKind === "recordList") {
    const webhookAction = findWebhookAction(looping, allActions);
    return webhookAction === undefined
      ? undefined
      : getOutputDescriptions(webhookAction).filter(
          supportsActionOutputLooping,
        );
  }

  if (looping.source?.oneofKind !== "outputReference") {
    return undefined;
  }

  switch (looping.source.outputReference.source.oneofKind) {
    case "actionReference": {
      const webhookAction = findWebhookAction(looping, allActions);
      if (webhookAction === undefined) {
        return undefined;
      }
      return getOutputDescriptions(webhookAction).filter(
        supportsActionOutputLooping,
      );
    }
    case "triggerReference":
      return isRecordTriggerSource(trigger)
        ? getOutputDescriptions(trigger).filter(supportsRecordFieldLooping)
        : getOutputDescriptions(trigger);
    default:
      return undefined;
  }
};
